import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Spin, Button, Tag, Card, Row, Col, Typography, Avatar, Alert, Empty,
} from "antd";
import {
  UserOutlined, EnvironmentOutlined, ClockCircleOutlined,
  CalendarOutlined, DollarOutlined, TeamOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../const";

const { Title, Text, Paragraph } = Typography;

const PublicBenchShare = () => {
  const { shareSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [gone, setGone] = useState(null);

  useEffect(() => {
    const fetchShare = async () => {
      try {
        const resp = await axios.get(`${API_CONST.BENCH_SHARE_PUBLIC}/${shareSlug}`);
        setData(resp.data);
      } catch (err) {
        if (err.response?.status === 410) {
          setGone(err.response.data?.message || "This link is no longer active.");
        } else if (err.response?.status === 404) {
          setGone("This share link was not found.");
        } else {
          setError("Failed to load bench pool. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchShare();
  }, [shareSlug]);

  const handleSignup = () => {
    const params = new URLSearchParams({ benchShareToken: shareSlug });
    const referralCode = data?.share?.referralCode;
    if (referralCode) params.set('ref', referralCode);
    navigate(`/signup?${params.toString()}`);
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f5ff" }}>
        <Spin size="large" tip="Loading bench pool…" />
      </div>
    );
  }

  if (gone || error) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f5ff", padding: 24 }}>
        <Card style={{ maxWidth: 480, width: "100%", textAlign: "center", borderRadius: 12 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <Title level={3} style={{ color: "#014c75" }}>Link Unavailable</Title>
          <Paragraph style={{ color: "#666" }}>{gone || error}</Paragraph>
          <Button type="primary" onClick={() => navigate("/")} style={{ background: "#014c75" }}>
            Go to HireNXT
          </Button>
        </Card>
      </div>
    );
  }

  const { share, talents } = data || {};
  const vf = share?.visibleFields || {};

  return (
    <div style={{ minHeight: "100vh", background: "#f0f5ff", fontFamily: "Arial, sans-serif" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #014c75 0%, #00d9a9 100%)", padding: "32px 24px 40px", textAlign: "center" }}>
        <div style={{ marginBottom: 8 }}>
          <Tag color="cyan" style={{ fontSize: 12, fontWeight: 600 }}>Powered by HireNXT</Tag>
        </div>
        <Title level={2} style={{ color: "#fff", margin: "0 0 8px" }}>{share?.title}</Title>
        <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 14 }}>
          Shared by <strong>{share?.partnerOrganization}</strong>
        </Text>
        {share?.message && (
          <Paragraph style={{ color: "rgba(255,255,255,0.9)", fontSize: 14, marginTop: 16, maxWidth: 600, margin: "16px auto 0" }}>
            {share.message}
          </Paragraph>
        )}
      </div>

      {/* Talent count pill */}
      <div style={{ textAlign: "center", marginTop: -20 }}>
        <Tag
          color="white"
          style={{ fontSize: 13, fontWeight: 600, padding: "6px 20px", borderRadius: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.12)", color: "#014c75", border: "1px solid #014c75" }}
        >
          {talents?.length || 0} talent{(talents?.length || 0) !== 1 ? "s" : ""} available
        </Tag>
      </div>

      {/* Talent grid */}
      <div style={{ maxWidth: 1100, margin: "32px auto", padding: "0 24px 120px" }}>
        {talents?.length === 0 ? (
          <Empty description="No talents in this bench pool" style={{ marginTop: 60 }} />
        ) : (
          <Row gutter={[20, 20]}>
            {talents.map((talent, idx) => (
              <Col key={idx} xs={24} sm={12} md={8} lg={6}>
                <Card
                  bordered={false}
                  style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", height: "100%" }}
                  bodyStyle={{ padding: 20 }}
                >
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
                    <Avatar size={44} icon={<UserOutlined />} style={{ background: "#e6f7ff", color: "#014c75", flexShrink: 0 }} />
                    <div style={{ marginLeft: 12, overflow: "hidden" }}>
                      <div style={{ fontWeight: 700, color: "#014c75", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {talent.name}
                      </div>
                      {vf.role && talent.role && (
                        <div style={{ color: "#555", fontSize: 12 }}>{talent.role}</div>
                      )}
                    </div>
                  </div>

                  {vf.skills && talent.primarySkills?.length > 0 && (
                    <div style={{ marginBottom: 6 }}>
                      <div style={{ fontSize: 11, color: "#888", marginBottom: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Primary</div>
                      {talent.primarySkills.slice(0, 3).map((s, i) => (
                        <Tag key={i} color="blue" style={{ fontSize: 11, marginBottom: 4 }}>{s}</Tag>
                      ))}
                    </div>
                  )}
                  {vf.secondarySkills && talent.secondarySkills?.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11, color: "#888", marginBottom: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Secondary</div>
                      {talent.secondarySkills.slice(0, 3).map((s, i) => (
                        <Tag key={i} color="default" style={{ fontSize: 11, marginBottom: 4 }}>{s}</Tag>
                      ))}
                    </div>
                  )}

                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {vf.experience && talent.experience && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#666", fontSize: 12 }}>
                        <ClockCircleOutlined style={{ color: "#00d9a9" }} />
                        <span>{talent.experience}</span>
                      </div>
                    )}
                    {vf.availability && talent.availability && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#666", fontSize: 12 }}>
                        <CalendarOutlined style={{ color: "#00d9a9" }} />
                        <span>{talent.availability}</span>
                      </div>
                    )}
                    {vf.location && talent.location && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#666", fontSize: 12 }}>
                        <EnvironmentOutlined style={{ color: "#00d9a9" }} />
                        <span>{talent.location}</span>
                      </div>
                    )}
                    {vf.clientRate && talent.clientRate != null && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: "#00d9a9" }}>
                        <DollarOutlined />
                        <span>₹{(talent.clientRate).toLocaleString("en-IN")}/mo</span>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>

      {/* Sticky CTA bar */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        background: "#fff", borderTop: "1px solid #e0e0e0",
        padding: "16px 24px", textAlign: "center",
        boxShadow: "0 -4px 16px rgba(0,0,0,0.08)", zIndex: 100,
      }}>
        <div style={{ maxWidth: 700, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
          <div>
            <Text style={{ color: "#014c75", fontWeight: 600, fontSize: 14 }}>
              <TeamOutlined style={{ marginRight: 6 }} />
              Interested in this talent?
            </Text>
            <Text style={{ color: "#666", fontSize: 13, marginLeft: 8 }}>
              Sign up for free to see full profiles and contact the team.
            </Text>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Button
              type="primary"
              size="large"
              onClick={handleSignup}
              style={{ background: "#014c75", borderColor: "#014c75", fontWeight: 600 }}
            >
              Sign Up Free
            </Button>
            <Button
              size="large"
              onClick={() => navigate("/")}
              style={{ fontWeight: 600 }}
            >
              Log In
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicBenchShare;
